/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useMemo, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Inline, Pressable, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ResearchRotatingThinkingIndicator } from '../loading-state/research-rotating-thinking-indicator/ResearchRotatingThinkingIndicator';
import { ResearchExpandable } from '../research-expandable';

import type { CompactResearchStep, TraceInteractionEvent } from './CompactResearchSteps';
import { TRACE_INTERACTION_TYPES } from './CompactResearchSteps';
import messages from './messages';
import { ResearchStepItemListRefresh as ResearchStepItemList } from './ResearchStepItemListRefresh';

const timelineConnectorLineStyle = cssUnbounded({
	position: 'absolute',
	left: '11px',
	top: '-12px',
	bottom: token('space.300'),
	width: '2px',
	backgroundColor: token('color.border'),
	zIndex: 0,
});

const timelineConnectorLineExpandedStyle = cssUnbounded({
	position: 'absolute',
	left: '11px',
	top: '28px',
	bottom: token('space.300'),
	width: '2px',
	backgroundColor: token('color.border'),
	zIndex: 0,
});

const styles = cssMap({
	researchStepList: {
		listStyleType: 'none',
		paddingInline: token('space.0'),
		marginBlock: token('space.0'),
		position: 'relative',
		overflow: 'visible',
	},
	thinkingListItem: {
		marginBottom: token('space.025'),
		// Override Confluence editor global li styles that add bullet points (e.g. in mini modal)
		listStyle: 'none',
	},
	stepTitleContainer: {
		marginBottom: token('space.0'),
		width: '100%',
	},
	clickableThinkingText: {
		cursor: 'pointer',
		backgroundColor: 'transparent',
		textAlign: 'left',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		overflow: 'hidden',
		minWidth: '0px',
	},
	unstyledPressable: {
		backgroundColor: 'transparent',
		paddingTop: token('space.150'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		border: 'none',
		width: '100%',
		textAlign: 'left',
		justifyContent: 'flex-start',
		alignItems: 'flex-start',
	},
	collapsedExpandable: {},
});

type ResearchCompactPreviewProps = {
	title: React.ReactElement | string;
	separatedContent?: string[];
	steps?: CompactResearchStep[];
	renderSubstep: (substep: string, isStreamed?: boolean) => React.ReactNode;
	researchCompleted?: boolean;
	thinkDeeperTriggered?: boolean;
	/** Whether the last step's content is streamed. Used for the preview substep display. */
	isLastStepStreamed?: boolean;
	/** Optional slot to render alongside the thinking message header (e.g. Answer Now button) */
	answerNowSlot?: React.ReactNode;
	/** Called when the user clicks to expand/collapse a trace step. */
	onTraceInteraction?: (event: TraceInteractionEvent) => void;
	/**
	 * Called when the user manually expands a step (expanded=true) or
	 * collapses a step (expanded=false) within this preview.
	 * CompactResearchSteps uses this to accumulate expand tokens for the
	 * completed phase.
	 */
	onUserExpandToggle?: (stepId: string, expanded: boolean) => void;
	/**
	 * Called when the preview's main expandable is opened or collapsed.
	 * CompactResearchSteps uses this to auto-open the outer steps-summary
	 * expandable post-streaming (on expand) or to clear all accumulated
	 * expand requests (on collapse).
	 */
	onPreviewExpandChanged?: (expanded: boolean) => void;
};

const MAX_VISIBLE_STEPS = 3;

/**
 * Multi-trace compact preview for think deeper traces.
 * Shows a sliding window of up to 3 traces (prioritising loading ones),
 * with animations as traces arrive and complete.
 */
export const ResearchCompactPreview = ({
	steps = [],
	renderSubstep,
	researchCompleted: _researchCompleted = false,
	thinkDeeperTriggered = false,
	answerNowSlot,
	onTraceInteraction,
	onUserExpandToggle,
	onPreviewExpandChanged,
}: ResearchCompactPreviewProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	// Single preview-local expand request: when the user expands a step in the
	// windowed (collapsed) section, we create a token so the same step
	// auto-expands inside the main expandable. Only one is needed because the
	// main expandable opens immediately and the token is consumed right away.
	const [previewExpandRequest, setPreviewExpandRequest] = useState<{
		stepId: string;
		token: React.MutableRefObject<boolean>;
	} | null>(null);

	// When a step is manually toggled from the windowed preview, open the
	// main expandable so the user immediately sees the expanded step.
	const handleWindowedUserExpandToggle = useCallback(
		(stepId: string, expanded: boolean) => {
			if (expanded) {
				setPreviewExpandRequest({ stepId, token: { current: true } });
				setIsExpanded(true);
				onPreviewExpandChanged?.(true);
			} else {
				setPreviewExpandRequest(null);
			}
			// Propagate to parent (CompactResearchSteps) for the completed-phase tokens
			onUserExpandToggle?.(stepId, expanded);
		},
		[onUserExpandToggle, onPreviewExpandChanged],
	);

	// When the user collapses the main expandable, clear the preview token
	// and notify the parent to clear all accumulated expand requests.
	const handleMainExpandableCollapsed = useCallback(() => {
		setIsExpanded(false);
		setPreviewExpandRequest(null);
		onPreviewExpandChanged?.(false);
		onTraceInteraction?.({
			type: TRACE_INTERACTION_TYPES.ALL_TRACES,
			expanded: false,
		});
	}, [onTraceInteraction, onPreviewExpandChanged]);

	const hasSteps = steps.length > 0;

	// Priority-based sliding window: always show latest loading traces, fill remaining with recent completed
	const windowedSteps = useMemo(() => {
		const loading = steps.filter((s) => !s.completed);
		const completed = steps.filter((s) => s.completed);

		// Take the most recent loading traces (up to MAX_VISIBLE_STEPS)
		const selectedLoading = loading.slice(-MAX_VISIBLE_STEPS);

		// Fill remaining slots with most recent completed traces
		const remainingSlots = MAX_VISIBLE_STEPS - selectedLoading.length;
		const selectedCompleted = remainingSlots > 0 ? completed.slice(-remainingSlots) : [];

		// Combine and sort back to original order
		const selected = [...selectedLoading, ...selectedCompleted];
		return selected.sort((a, b) => steps.indexOf(a) - steps.indexOf(b));
	}, [steps]);

	return (
		<Box>
			<Stack space="space.0" grow="fill">
				{hasSteps ? (
					<Box xcss={!isExpanded ? styles.collapsedExpandable : undefined}>
						<ResearchExpandable
							defaultExpanded={isExpanded}
							showExpandIcon={!isExpanded}
							showExpandAtEnd={false}
							showDownRightIcons={true}
							iconSpacing="compact"
							onExpandChanged={(expanded) => setIsExpanded(expanded)}
							onExpandClick={(expanded) => {
								onTraceInteraction?.({ type: TRACE_INTERACTION_TYPES.ALL_TRACES, expanded });
								onPreviewExpandChanged?.(expanded);
							}}
							title={
								isExpanded ? null : (
									<ResearchRotatingThinkingIndicator
										steps={steps}
										researchCompleted={_researchCompleted}
										thinkDeeperTriggered={thinkDeeperTriggered}
										isAnimated={false}
										icon={false}
										animation={true}
										showEllipsis={true}
									/>
								)
							}
							afterExpandIconSlot={!isExpanded ? answerNowSlot : undefined}
						>
							<Box as="ul" xcss={styles.researchStepList} testId="research-step-list">
								{steps.length > 0 && <div css={timelineConnectorLineExpandedStyle} />}
								{isExpanded && (
									<Box as="li" xcss={styles.thinkingListItem} testId="research-step-thinking-item">
										<Box xcss={styles.stepTitleContainer}>
											<Inline spread="space-between" alignBlock="center" space="space.050">
												<Pressable
													onClick={handleMainExpandableCollapsed}
													aria-label={formatMessage(messages.collapse)}
													xcss={styles.clickableThinkingText}
												>
													<ResearchRotatingThinkingIndicator
														steps={steps}
														researchCompleted={_researchCompleted}
														thinkDeeperTriggered={thinkDeeperTriggered}
														isAnimated={false}
														icon={false}
														animation={true}
														showEllipsis={true}
													/>
												</Pressable>
												<Inline space="space.050" alignBlock="center">
													<IconButton
														shape="circle"
														appearance="subtle"
														icon={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
														label={formatMessage(messages.collapse)}
														onClick={handleMainExpandableCollapsed}
													/>
													{answerNowSlot}
												</Inline>
											</Inline>
										</Box>
									</Box>
								)}
								{steps.map((step, index) => {
									const isLastStep = index === steps.length - 1;
									// Use individual step completion status: completed → NodeIcon, loading → Spinner
									const isInProgress = !step.completed;
									const lastSubstepForStep =
										step.separatedContent.length > 0
											? step.separatedContent[step.separatedContent.length - 1]
											: undefined;
									return (
										<ResearchStepItemList
											key={step.stepId}
											step={step}
											isLastItem={isLastStep}
											isInProgress={isInProgress}
											hideIcon={false}
											disableShimmer={true}
											forceSubstepCollapsed={true}
											previewSubstep={lastSubstepForStep}
											titleSize="small"
											titleWeight="medium"
											expandRequest={
												previewExpandRequest?.stepId === step.stepId
													? previewExpandRequest.token
													: null
											}
											onUserExpandToggle={onUserExpandToggle}
											renderSubstep={renderSubstep}
											onTraceInteraction={onTraceInteraction}
										/>
									);
								})}
							</Box>
						</ResearchExpandable>
					</Box>
				) : null}
				{!isExpanded && hasSteps && (
					<Pressable
						xcss={styles.unstyledPressable}
						onClick={(e) => {
							if (e.target instanceof Element) {
								// Don't expand all when clicking an individual substep expand button
								if (e.target.closest('button')) {
									return;
								}
								// Don't hijack clicks on actual links within the substep
								if (e.target.closest('a[href]')) {
									return;
								}
							}
							setIsExpanded(true);
							onPreviewExpandChanged?.(true);
							onTraceInteraction?.({ type: TRACE_INTERACTION_TYPES.ALL_TRACES, expanded: true });
						}}
					>
						<Box as="ul" xcss={styles.researchStepList}>
							{windowedSteps.length > 0 && <div css={timelineConnectorLineStyle} />}
							{windowedSteps.map((step, index) => {
								const isLastStep = index === windowedSteps.length - 1;
								// Use individual step completion status: completed → NodeIcon, loading → Spinner
								const isInProgress = !step.completed;
								const lastSubstep =
									step.separatedContent.length > 0
										? step.separatedContent[step.separatedContent.length - 1]
										: undefined;
								return (
									<ExitingPersistence key={step.stepId}>
										<FadeIn entranceDirection="bottom" exitDirection="top" duration="medium">
											{(fadeProps) => (
												<div {...fadeProps}>
													<ResearchStepItemList
														step={step}
														isLastItem={isLastStep}
														isInProgress={isInProgress}
														hideIcon={false}
														disableShimmer={true}
														forceSubstepCollapsed={true}
														previewSubstep={lastSubstep}
														titleSize="small"
														titleWeight="medium"
														onUserExpandToggle={handleWindowedUserExpandToggle}
														renderSubstep={renderSubstep}
														onTraceInteraction={onTraceInteraction}
													/>
												</div>
											)}
										</FadeIn>
									</ExitingPersistence>
								);
							})}
						</Box>
					</Pressable>
				)}
			</Stack>
		</Box>
	);
};
